/**
 * The time frame every billing panel reports on (Billing AI, Billing Amount,
 * Usage History): the current billing period (default), a past billing period
 * from the usage archive, or a custom date range.
 *
 * The backend resolves the same window for all three panels
 * (backend shared/limits/usage-window.util.ts), so a given choice always
 * yields the same numbers everywhere. The choice is kept for the browser tab,
 * so switching between billing pages keeps the same window.
 */
import React, { useEffect, useState } from 'react';
import { Box, FormControl, InputLabel, MenuItem, Select, Stack } from '@mui/material';
import DateRangePicker from '@/components/analytics/DateRangePicker';

export type BillingTimeframe =
  | { kind: 'current' }
  /** A past billing period [start, end) as ISO instants, from the usage archive. */
  | { kind: 'period'; start: string; end: string }
  /** A custom range: LOCAL calendar days, both inclusive (YYYY-MM-DD). */
  | { kind: 'custom'; start: string; end: string };

const STORAGE_KEY = 'billing.timeframe';

function readStored(): BillingTimeframe {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    const v = raw ? JSON.parse(raw) : null;
    if (v?.kind === 'period' || v?.kind === 'custom') {
      if (typeof v.start === 'string' && typeof v.end === 'string') return v;
    }
  } catch {
    // sessionStorage can be unavailable (private windows, blocked site data).
  }
  return { kind: 'current' };
}

/** Time-frame state shared by the billing pages for the lifetime of the tab. */
export function useBillingTimeframe(): [BillingTimeframe, (tf: BillingTimeframe) => void] {
  const [tf, setTf] = useState<BillingTimeframe>(readStored);
  const update = (next: BillingTimeframe) => {
    setTf(next);
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      // non-fatal — the choice just won't carry to the next billing page
    }
  };
  return [tf, update];
}

/** UTC calendar day of an instant (billing periods are UTC-midnight bounded). */
const utcDay = (iso: string | number) => new Date(iso).toISOString().slice(0, 10);

/**
 * Query params for the usage APIs. `current` sends nothing, so the backend
 * applies the tenant's live period. A period `[start, end)` becomes the
 * inclusive days `start … end-1ms`.
 */
export function timeframeParams(tf: BillingTimeframe): { startDate?: string; endDate?: string } {
  if (tf.kind === 'period') return { startDate: utcDay(tf.start), endDate: utcDay(Date.parse(tf.end) - 1) };
  if (tf.kind === 'custom') return { startDate: tf.start, endDate: tf.end };
  return {};
}

const fmtUtc = (iso: string | number) =>
  new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });

/** "15 Sep 2026 – 14 Oct 2026" for a period [start, end). */
export function formatPeriod(start: string, end: string): string {
  return `${fmtUtc(start)} – ${fmtUtc(Date.parse(end) - 1)}`;
}

function toCalendarDay(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
function fromCalendarDay(s: string, endOfDay = false): Date {
  const [y, m, d] = s.split('-').map(Number);
  return endOfDay ? new Date(y!, m! - 1, d!, 23, 59, 59, 999) : new Date(y!, m! - 1, d!);
}

interface Props {
  value: BillingTimeframe;
  onChange: (tf: BillingTimeframe) => void;
  /** The live period, from the last usage response (label only). */
  currentPeriod?: { start: string; end: string } | null | undefined;
  /** Past billing periods, most recent first. */
  loadPeriods: () => Promise<Array<{ periodStart: string; periodEnd: string }>>;
}

const BillingTimeframeSelect: React.FC<Props> = ({ value, onChange, currentPeriod, loadPeriods }) => {
  const [periods, setPeriods] = useState<Array<{ periodStart: string; periodEnd: string }>>([]);

  useEffect(() => {
    let cancelled = false;
    loadPeriods()
      .then((rows) => {
        if (!cancelled) setPeriods(rows || []);
      })
      .catch(() => {
        // Non-fatal: the current period and a custom range still work.
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectValue = value.kind === 'period' ? `period:${value.start}` : value.kind;

  const handleSelect = (key: string) => {
    if (key === 'current') return onChange({ kind: 'current' });
    if (key === 'custom') {
      const end = new Date();
      const start = new Date(end.getTime() - 29 * 24 * 60 * 60 * 1000);
      return onChange({ kind: 'custom', start: toCalendarDay(start), end: toCalendarDay(end) });
    }
    const p = periods.find((row) => `period:${row.periodStart}` === key);
    if (p) onChange({ kind: 'period', start: p.periodStart, end: p.periodEnd });
  };

  // A stored past period may not be in the loaded list yet; keep it selectable.
  const periodOptions =
    value.kind === 'period' && !periods.some((p) => p.periodStart === value.start)
      ? [{ periodStart: value.start, periodEnd: value.end }, ...periods]
      : periods;

  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }}>
      <FormControl size="small" sx={{ minWidth: 260 }}>
        <InputLabel>Time frame</InputLabel>
        <Select label="Time frame" value={selectValue} onChange={(e) => handleSelect(String(e.target.value))}>
          <MenuItem value="current">
            {currentPeriod
              ? `Current billing period · ${formatPeriod(currentPeriod.start, currentPeriod.end)}`
              : 'Current billing period'}
          </MenuItem>
          {periodOptions.map((p) => (
            <MenuItem key={p.periodStart} value={`period:${p.periodStart}`}>
              {formatPeriod(p.periodStart, p.periodEnd)}
            </MenuItem>
          ))}
          <MenuItem value="custom">Custom range…</MenuItem>
        </Select>
      </FormControl>
      {value.kind === 'custom' && (
        <Box>
          <DateRangePicker
            startDate={fromCalendarDay(value.start)}
            endDate={fromCalendarDay(value.end, true)}
            onChange={(start, end) =>
              onChange({ kind: 'custom', start: toCalendarDay(start), end: toCalendarDay(end) })
            }
          />
        </Box>
      )}
    </Stack>
  );
};

export default BillingTimeframeSelect;
